import { CompactUserResponse } from '@business/entities/account';
import { CompanyId } from '@business/entities/companies';
import { ExtendedFile } from '../common';
import { UserId } from './../account/UserResponse';
import { BaseResponse } from './../common/BaseResponse';

export type FolderId = string;
export type ItemId = string;

export interface LibraryFolderBreadcrumbResponse {
  id: string;
  name: string;
}

export interface LibraryItemsResponse {
  breadcrumbs: LibraryFolderBreadcrumbResponse[];
  items: (LibraryResponse | LibraryItemResponse)[];
}

export interface LibraryResponse extends BaseResponse {
  id: FolderId;
  name: string;
  type: LibraryItemType;
  isPolicy: boolean;
  isArticle: boolean;
  isChart: boolean;
  items: number;
  user: CompactUserResponse;
  parent?: LibraryResponse[];
  breadcrumbs?: LibraryFolderBreadcrumbResponse[];
  parentFolderId: FolderId;
  shares: LibraryItemShareItem[];
  description?: string;
  files?: ExtendedFile;
  assignee?: string[];
  tags?: string[];
}

export interface JobDescriptionFunctionResponse extends BaseResponse {
  function: string;
  authorityLevel: JobAuthorify;
}

export interface JobDescriptionResponse extends BaseResponse {
  overview: string;
  youReports: string;
  reportYou: string;
  functions: JobDescriptionFunctionResponse[];
  education: string;
  experience: string;
  skills: string;
  physicalRequirements: string;
}

export type LibraryVersionId = string;

export interface LibraryItemResponse extends BaseResponse {
  id: ItemId;
  name: string;
  content: string | JobDescriptionResponse;
  type: LibraryItemType;
  user: CompactUserResponse;
  status: LibraryItemStatus;
  folder?: LibraryResponse[];
  files: ExtendedFile[];
  parentFolderId: FolderId;
  description?: string;
  assignee?: string[];
  shares: LibraryItemShareItem[];
  filePath: string;
  tags?: string[];
}

export enum LibraryItemShareType {
  item = 'item',
  folder = 'folder',
}

export interface LibraryItemShareItem {
  userId: UserId;
  view: boolean;
  manage: boolean;
  type: LibraryItemShareType;
}

export interface LibraryItemAttachedFile {
  fileName: string;
  filePath: string;
}
export interface CompactLibraryItemResponse extends BaseResponse {
  id: ItemId;
  name: string;
  content: string | JobDescriptionResponse;
  type: LibraryItemType;
  user: CompactUserResponse;
  status: LibraryItemStatus;
  parentFolderId: FolderId;
  files: ExtendedFile[];
  description?: string;
  shares: LibraryItemShareItem[];
  filePath: string;
  assignee?: string[];
  tags?: string[];
}

export type ItemRequest = Pick<LibraryItemResponse, 'name' | 'content' | 'type' | 'folder'>;

export enum LibraryItemStatus {
  public = 'public',
  private = 'private',
  draft = 'draft',
}

export enum LibraryItemType {
  folder = 'folder',
  article = 'article',
  policy = 'policy',
  procedure = 'procedure',
  file = 'file',
  documentManagement = 'documentManagement',
}

export enum LibraryItemTypeDropDown {
  all = 'all',
  folder = 'folder',
  article = 'article',
  policy = 'policy',
  procedure = 'procedure',
  file = 'file',
  documentManagement = 'documentManagement',
}

export enum CompanyPolicyStatus {
  publish = 1,
  draft = 2,
  approval = 3,
}

export function getLibraryItemStatus(status: CompanyPolicyStatus) {
  switch (status) {
    case 1:
      return LibraryItemStatus.public;
    case 2:
      return LibraryItemStatus.private;
    case 3:
      return LibraryItemStatus.draft;
  }
}
export interface CompactLibraryFolderResponse {
  id: FolderId;
  name: string;
}

export const LibraryItemStatuses = [LibraryItemStatus.public, LibraryItemStatus.private, LibraryItemStatus.draft];
export const LibraryItemTypes = [LibraryItemType.folder, LibraryItemType.policy];

export const LibraryItemStatusColor: Record<LibraryItemStatus, string> = {
  private: 'dark',
  public: 'success',
  draft: 'info',
};

export const LibraryItemTypeIcon: Record<LibraryItemType, string> = {
  folder: 'bx bx-folder',
  policy: 'bx bx-file',
  article: 'bx bx-file-blank',
  procedure: 'bx bx-book',
  file: 'bx bx-file-blank',
  documentManagement: 'bx bxs-file-md',
};

export const LibraryItemTypeColor: Record<LibraryItemType, string> = {
  folder: 'primary',
  policy: 'secondary',
  article: 'secondary',
  procedure: 'secondary',
  file: 'secondary',
  documentManagement: 'secondary',
};

export interface CompactLibrarySearchResponse {
  id: FolderId;
  name: string;
  type: LibraryItemType;
}

export interface LibraryFolderStructureResponse {
  id: string;
  name: string;
}

export enum JobAuthorify {
  getApproval = 'getApproval',
  handle = 'handle',
  act = 'act',
}

export const JOB_DESCRIPTION_AUTHORITIES: Record<JobAuthorify, string> = {
  getApproval: 'Inform and then act (Get approval)',
  handle: 'Act and then inform (Handle, but inform)',
  act: 'Act (Just take care of it)',
};

export interface PublicLibraryItemResponse extends LibraryItemResponse {
  logo: string;
}

export type CompanyPolicyId = string;
export interface CompanyPolicyResponse extends BaseResponse {
  id: CompanyPolicyId;
  companyId: CompanyId;
  userId: UserId;
  name: string;
  description: string;
  type: LibraryItemType;
  status: LibraryItemStatus;
  tags: string[];
}

export interface CompactCompanyPolicyResponse {
  id: CompanyPolicyId;
  name: string;
  type: LibraryItemType;
}

export enum LibraryItemAssigneeType {
  user = 'user',
  group = 'group',
}

export const LibraryItemNameIcon = {
  jpg: 'bx bxs-file-image',
  jpeg: 'bx bxs-file-image',
  png: 'bx bxs-file-image',
  svg: 'bx bxs-file-image',
  csv: 'bx bx-spreadsheet',
  xlsx: 'bx bx-spreadsheet',
  xls: 'bx bx-spreadsheet',
  xlsm: 'bx bx-spreadsheet',
  xlsb: 'bx bx-spreadsheet',
  xltx: 'bx bx-spreadsheet',
  pdf: 'bx bxs-file-pdf',
  docx: 'bx bxs-file-doc',
  doc: 'bx bxs-file-doc',
  other: 'bx bx-file-blank',
};
